import { combineReducers } from "redux";
import { reducer as urlFiltersReducer } from "../pages/urlFilters/urlFilters.reducer";
import { reducer as loaderReducer } from "../components/loader/loader.reducer";
import { reducer as notificationReducer } from "../components/notification";
import { reducer as profilesReducer } from "./profiles/profiles.reducer";
import { reducer as ssProfilesReducer } from "../pages/profiles/components/internetSecurity/subscriberSafety/ssProfiles.reducer";
import { reducer as reportsReducer } from "./reports/reports.reducer";
import { reducer as webFiltersReducer } from "../pages/profiles/components/webFilters/webFilters.reducer";
import { reducer as domainLookup } from "../pages/domainLookup/store";
import { InfoState, subscriberInfoReducer } from "./info/info.reducer";
import accountSettingsReducer from "./attributes/attributes.reducer";
import { reducer as resourcesReducer } from "../pages/blockPageContent/resources.reducer";
import settingsReducer from "./settings/settings.reducer";
import accountReducer from "./account/account.reducer";
import initialReducer from "./initial/initial.reducer";
import { reducer as categoriesReducer } from "./categories/categories.reducer";
import { reducer as protectionsReducer } from "./protections/protections.reducer";
import devicesReducer from "./devices/devices.reducer";
import { threatsReducer } from "./threats";
import { reducer as schedulesReducer } from "../pages/profiles/components/schedule/schedules.reducer";
import { reducer as helpReducer } from "../pages/help/help.reducer";
import translationReducer from "../i18n/translation.reducer";
import { bannersReducer } from "./banners/banners.reducer";
import { reducer as wizardReducer } from "./wizard/wizard.reducer";
import { reducer as LayoutReducer } from "./layout/layout.reducer";
import { deeplinkReducer } from "../components/deeplinkDialog/deeplinkDialog.reducer";
import { reducer as reminderReducer } from "./reminder/reminder.reducer";

import {
  LOGIN_FAILURE,
  LOGIN_SUCCESS,
  RESET_USER,
  SET_SESSION_EXPIRED,
  RESET_SESSION_EXPIRED,
} from "../pages/login/login.actions";

const featureReducers = combineReducers({
  config: (state: any = {}) => state,
  isSessionExpired: (state) => state || false,
  initialLoad: initialReducer,
  translation: translationReducer,
  loader: loaderReducer,
  notifications: notificationReducer,
  subscriberInfo: subscriberInfoReducer,
  settings: settingsReducer,
  account: accountReducer,
  accountSettings: accountSettingsReducer,
  resources: resourcesReducer,
  profiles: profilesReducer,
  ssProfiles: ssProfilesReducer,
  webFilters: webFiltersReducer,
  schedules: schedulesReducer,
  categories: categoriesReducer,
  protections: protectionsReducer,
  urlFilters: urlFiltersReducer,
  domainLookup: domainLookup,
  reports: reportsReducer,
  devices: devicesReducer,
  help: helpReducer,
  threats: threatsReducer,
  banners: bannersReducer,
  wizard: wizardReducer,
  layout: LayoutReducer,
  deepLink: deeplinkReducer,
  reminder: reminderReducer,
});

type FeatureState = ReturnType<typeof featureReducers>;

const rootReducer = (state: FeatureState, action) => {
  switch (action.type) {
    case LOGIN_FAILURE: {
      const {
        subscriberInfo,
        config,
        loader,
        translation,
        help,
        notifications,
        layout,
      } = state;

      return {
        ...featureReducers(undefined, action),
        subscriberInfo: {
          ...subscriberInfo,
          isLoading: false,
          success: false,
          error: action.payload,
        },
        config,
        loader,
        translation,
        help,
        notifications,
        layout,
      };
    }
    case RESET_USER:
      const {
        subscriberInfo,
        config,
        loader,
        translation,
        help,
        notifications: { list },
      } = state;

      return {
        ...featureReducers(undefined, action),
        subscriberInfo: { authMethod: subscriberInfo.authMethod } as InfoState,
        config,
        loader,
        translation,
        help,
        notifications: { list },
      };
    case SET_SESSION_EXPIRED: {
      const {
        subscriberInfo,
        config,
        loader,
        translation,
        help,
        notifications: { list },
      } = state;

      return {
        ...featureReducers({} as any, action),
        isSessionExpired: true,
        subscriberInfo: { authMethod: subscriberInfo.authMethod } as InfoState,
        config,
        loader,
        translation,
        help,
        notifications: { list },
      };
    }
    case RESET_SESSION_EXPIRED: {
      return { ...state, isSessionExpired: false };
    }
    case LOGIN_SUCCESS:
      return {
        ...state,
        subscriberInfo: subscriberInfoReducer(undefined, action),
      };
    default:
      return {
        ...state,
        ...featureReducers(state, action),
      };
  }
};

export type RootState = ReturnType<typeof rootReducer>;

export default rootReducer;
